/**
 * DocumentPreview Component
 *
 * Displays uploaded lab result documents (PDF or images) in an inline viewer.
 * Supports multiple documents with tab navigation.
 * Uses react-pdf for proper PDF rendering with zoom controls.
 */
import { useState, useEffect, useCallback } from 'react'
import { Card, Tabs, Image, Button, Space, Spin, Empty, Tooltip } from 'antd'
import {
  ExpandOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  ExportOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { Document as PdfDocument, Page as PdfPage, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface DocumentFile {
  id: string
  fileName: string
  fileUrl: string
  fileType: string
}

interface DocumentPreviewProps {
  documents: DocumentFile[]
  height?: number | string
}

/**
 * Detect if file is an image based on type or extension
 */
function isImageFile(doc: DocumentFile): boolean {
  const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  if (imageTypes.includes(doc.fileType.toLowerCase())) {
    return true
  }
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
  const fileName = doc.fileName.toLowerCase()
  return imageExtensions.some((ext) => fileName.endsWith(ext))
}

/**
 * Detect if file is a PDF
 */
function isPdfFile(doc: DocumentFile): boolean {
  if (doc.fileType.toLowerCase() === 'application/pdf') {
    return true
  }
  return doc.fileName.toLowerCase().endsWith('.pdf')
}

/**
 * Get icon for document type
 */
function getDocumentIcon(doc: DocumentFile) {
  if (isPdfFile(doc)) {
    return <FilePdfOutlined style={{ color: '#ff4d4f' }} />
  }
  if (isImageFile(doc)) {
    return <FileImageOutlined style={{ color: '#1890ff' }} />
  }
  return <FileOutlined />
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

/**
 * Get streaming URL for a storage key
 * Uses the API proxy endpoint to avoid MinIO internal hostname issues
 */
function getStreamingUrl(storageKey: string): string {
  // If already a full URL, extract the key portion
  // Format: http://hostname:port/bucket/key
  if (storageKey.startsWith('http://') || storageKey.startsWith('https://')) {
    try {
      const url = new URL(storageKey)
      // Path is like /perfectamed-documents/lab-results/user-id/timestamp-file.pdf
      // We need to extract everything after the bucket name
      const pathParts = url.pathname.split('/')
      // Remove empty first element and bucket name
      if (pathParts.length > 2) {
        const key = pathParts.slice(2).join('/')
        return `${API_URL}/storage/file/${key}`
      }
    } catch {
      // Fall through to use as-is
    }
    return storageKey
  }

  // Storage key is already just the key, use streaming endpoint directly
  // Note: Token will be added via headers in fetch calls, but for direct URLs (like PDF src)
  // we need to use a different approach - fetch via API and get blob URL
  return `${API_URL}/storage/file/${storageKey}`
}

/**
 * Fetch file as blob URL with authentication
 */
async function getAuthenticatedBlobUrl(storageKey: string): Promise<string> {
  const streamingUrl = getStreamingUrl(storageKey)
  const token = localStorage.getItem('admin_access_token')

  const response = await fetch(streamingUrl, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  if (!response.ok) {
    throw new Error('Failed to fetch file')
  }

  const blob = await response.blob()
  return URL.createObjectURL(blob)
}

/**
 * PDF Viewer Component using react-pdf
 */
function PdfViewer({ storageKey, height }: { storageKey: string; height: number | string }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [url, setUrl] = useState<string | null>(null)
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(1.0)

  // Fetch blob URL on mount
  useEffect(() => {
    let cancelled = false
    let blobUrl: string | null = null
    setLoading(true)
    setError(false)
    setUrl(null)

    getAuthenticatedBlobUrl(storageKey)
      .then((fetchedBlobUrl) => {
        if (!cancelled) {
          blobUrl = fetchedBlobUrl
          setUrl(fetchedBlobUrl)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true)
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
      // Clean up blob URL to avoid memory leaks
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [storageKey])

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setLoading(false)
  }, [])

  const onDocumentLoadError = useCallback(() => {
    setError(true)
    setLoading(false)
  }, [])

  const goToPrevPage = () => setPageNumber((p) => Math.max(1, p - 1))
  const goToNextPage = () => setPageNumber((p) => Math.min(numPages, p + 1))
  const zoomIn = () => setScale((s) => Math.min(2.5, s + 0.25))
  const zoomOut = () => setScale((s) => Math.max(0.5, s - 0.25))
  const fitWidth = () => setScale(1.0)

  return (
    <div style={{ height, width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <div
        style={{
          height: 48,
          padding: '8px 12px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#fafafa',
        }}
      >
        {/* Page navigation */}
        <Space>
          <Button
            size="small"
            icon={<LeftOutlined />}
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
          />
          <span style={{ minWidth: 80, textAlign: 'center' }}>
            {pageNumber} / {numPages || '-'}
          </span>
          <Button
            size="small"
            icon={<RightOutlined />}
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
          />
        </Space>

        {/* Zoom controls */}
        <Space>
          <Tooltip title="Umanji">
            <Button size="small" icon={<ZoomOutOutlined />} onClick={zoomOut} disabled={scale <= 0.5} />
          </Tooltip>
          <span style={{ minWidth: 50, textAlign: 'center' }}>{Math.round(scale * 100)}%</span>
          <Tooltip title="Uvećaj">
            <Button size="small" icon={<ZoomInOutlined />} onClick={zoomIn} disabled={scale >= 2.5} />
          </Tooltip>
          <Tooltip title="Prilagodi širini">
            <Button size="small" icon={<FullscreenOutlined />} onClick={fitWidth} />
          </Tooltip>
        </Space>
      </div>

      {/* PDF Content */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: '#525659',
          display: 'flex',
          justifyContent: 'center',
          padding: '16px',
        }}
      >
        {loading && !url && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Spin tip="Učitavanje PDF-a..." />
          </div>
        )}

        {error ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Empty
              description={<span style={{ color: '#fff' }}>Ne mogu da prikažem PDF</span>}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button
                type="primary"
                icon={<ExportOutlined />}
                onClick={() => getAuthenticatedBlobUrl(storageKey).then((u) => window.open(u, '_blank'))}
              >
                Otvori u novom tabu
              </Button>
            </Empty>
          </div>
        ) : url ? (
          <PdfDocument
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
                <Spin tip="Učitavanje PDF-a..." />
              </div>
            }
          >
            <PdfPage
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </PdfDocument>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Image Viewer Component with zoom
 */
function ImageViewer({ storageKey, fileName }: { storageKey: string; fileName: string }) {
  const [zoom, setZoom] = useState(100)
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    let blobUrl: string | null = null
    setLoading(true)

    getAuthenticatedBlobUrl(storageKey)
      .then((fetchedBlobUrl) => {
        if (!cancelled) {
          blobUrl = fetchedBlobUrl
          setUrl(fetchedBlobUrl)
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [storageKey])

  const handleZoomIn = () => setZoom((z) => Math.min(z + 25, 200))
  const handleZoomOut = () => setZoom((z) => Math.max(z - 25, 50))

  if (loading) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin tip="Učitavanje slike..." />
      </div>
    )
  }

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '8px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <Tooltip title="Umanji">
          <Button
            size="small"
            icon={<ZoomOutOutlined />}
            onClick={handleZoomOut}
            disabled={zoom <= 50}
          />
        </Tooltip>
        <span style={{ lineHeight: '24px', minWidth: '50px', textAlign: 'center' }}>
          {zoom}%
        </span>
        <Tooltip title="Uvećaj">
          <Button
            size="small"
            icon={<ZoomInOutlined />}
            onClick={handleZoomIn}
            disabled={zoom >= 200}
          />
        </Tooltip>
      </div>
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          backgroundColor: '#fafafa',
        }}
      >
        {url && (
          <Image
            src={url}
            alt={fileName}
            style={{
              maxWidth: `${zoom}%`,
              maxHeight: '100%',
              objectFit: 'contain',
              transition: 'max-width 0.2s ease',
            }}
            preview={{
              mask: (
                <Space>
                  <ExpandOutlined /> Pun ekran
                </Space>
              ),
            }}
          />
        )}
      </div>
    </div>
  )
}

/**
 * Main DocumentPreview Component
 */
export function DocumentPreview({ documents, height = 500 }: DocumentPreviewProps) {
  const [activeTab, setActiveTab] = useState(documents[0]?.id || '')

  if (!documents || documents.length === 0) {
    return (
      <Card style={{ height }}>
        <Empty
          description="Nema dostupnih dokumenata"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    )
  }

  const activeDoc = documents.find((d) => d.id === activeTab) || documents[0]

  const tabItems = documents.map((doc) => ({
    key: doc.id,
    label: (
      <Space size={4}>
        {getDocumentIcon(doc)}
        <span
          style={{
            maxWidth: 120,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {doc.fileName}
        </span>
      </Space>
    ),
  }))

  const handleOpenInNewTab = async (storageKey: string) => {
    try {
      const blobUrl = await getAuthenticatedBlobUrl(storageKey)
      window.open(blobUrl, '_blank')
    } catch (error) {
      console.error('Failed to get file:', error)
    }
  }

  const renderDocument = (doc: DocumentFile) => {
    const viewerHeight =
      typeof height === 'number'
        ? height - (documents.length > 1 ? 100 : 60)
        : `calc(${height} - ${documents.length > 1 ? 100 : 60}px)`

    if (isPdfFile(doc)) {
      return <PdfViewer storageKey={doc.fileUrl} height={viewerHeight} />
    }

    if (isImageFile(doc)) {
      return (
        <div style={{ height: viewerHeight }}>
          <ImageViewer storageKey={doc.fileUrl} fileName={doc.fileName} />
        </div>
      )
    }

    // Fallback for unknown types
    return (
      <div
        style={{
          height: viewerHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Empty
          description="Format datoteke nije podržan za pregled"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button
            type="primary"
            icon={<ExportOutlined />}
            onClick={() => handleOpenInNewTab(doc.fileUrl)}
          >
            Preuzmi datoteku
          </Button>
        </Empty>
      </div>
    )
  }

  return (
    <Card
      style={{ height }}
      bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
      extra={
        <Tooltip title="Otvori u novom tabu">
          <Button
            type="text"
            size="small"
            icon={<ExportOutlined />}
            onClick={() => handleOpenInNewTab(activeDoc.fileUrl)}
          />
        </Tooltip>
      }
      title={
        <Space>
          {getDocumentIcon(activeDoc)}
          <span>Dokumenti</span>
        </Space>
      }
    >
      {documents.length > 1 ? (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          style={{ flex: 1 }}
          tabBarStyle={{ margin: 0, padding: '0 16px' }}
        />
      ) : null}
      <div style={{ flex: 1 }}>{renderDocument(activeDoc)}</div>
    </Card>
  )
}

export default DocumentPreview
